import { supabase } from "./supabase";
import { resolveCurrentSender } from './messagesCore';
import { recordOpportunityActivity } from './opportunityActivities';

const ALLOWED_DOMAIN = import.meta.env.VITE_EMAIL_DOMAIN || "gitbooked.com";

export function formatBusinessEmail(businessName: string): string {
  // Convert business name to lowercase and replace spaces with dots
  const formattedName = businessName
    .toLowerCase()
    .replace(/\s+/g, ".")
    .replace(/[^a-z0-9.]/g, "");

  return `${formattedName}@${ALLOWED_DOMAIN}`;
}

export function validateBusinessEmail(email: string): boolean {
  // Check if email ends with the allowed domain
  return email.endsWith(`@${ALLOWED_DOMAIN}`);
}

export async function updateBusinessEmail(
  businessId: string,
  email: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!validateBusinessEmail(email)) {
      return {
        success: false,
        error: `Email must end with @${ALLOWED_DOMAIN}`,
      };
    }

    if (!supabase) {
      return {
        success: false,
        error: "Supabase client is not initialized",
      };
    }

    const { error } = await supabase
      .from("businesses")
      .update({ business_domain_email: email })
      .eq("id", businessId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Error updating business email:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Failed to update business email",
    };
  }
}

interface SendEmailParams {
  to: string;
  subject: string;
  text: string;
  html?: string;
  businessId: string;
  from?: string; // Optional - will get business email if not provided
  conversationId?: string; // Optional - for tracking in communications
}

interface SendEmailResult {
  success: boolean;
  error?: string;
}

/**
 * Sends an email using the application's email system
 * Uses SendGrid via Supabase Edge Functions
 */
export async function sendEmail({
  to,
  subject,
  text,
  html,
  businessId,
  from,
  conversationId
}: SendEmailParams): Promise<SendEmailResult> {
  try {
    if (!supabase) {
      throw new Error("Supabase client is not initialized");
    }

    // If from email not provided, get the business email
    let fromEmail = from;
    if (!fromEmail) {
      const { data: business, error: businessError } = await supabase
        .from("businesses")
        .select("business_domain_email")
        .eq("id", businessId)
        .single();

      if (businessError) throw businessError;
      if (!business?.business_domain_email) {
        throw new Error("Business email not configured");
      }

      fromEmail = business.business_domain_email;
    }

    // Send email via Supabase Edge Function
    const { error } = await supabase.functions.invoke("send-email", {
      body: {
        to,
        subject,
        text,
        html,
        from: fromEmail,
        businessId
      },
    });

    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error("Error sending email:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Failed to send email",
    };
  }
}

/**
 * Simple Gmail email sending function for use throughout the app
 * Uses the Gmail edge function similar to how sendSMS works
 */
export interface SendGmailEmailParams {
  businessId: string;
  to: string;
  cc?: string;   // comma-separated Cc recipients
  bcc?: string;  // comma-separated Bcc recipients
  subject?: string;
  body: string;
  aiGenerated?: boolean;  // Add AI flag parameter
  threadId?: string;  // Gmail thread ID for replies
  inReplyTo?: string;  // Message-ID to reply to
  references?: string;  // References header for threading
  attachments?: Array<{
    filename: string;
    content: string;  // base64 encoded content
    type: string;     // MIME type like 'application/pdf'
  }>;
}

export interface SendGmailEmailResult {
  success: boolean;
  error?: string;
  messageId?: string;
  threadId?: string;
}

export async function sendGmailEmail({
  businessId,
  to,
  cc,
  bcc,
  subject,
  body,
  aiGenerated = false,  // Default to false if not provided
  threadId,
  inReplyTo,
  references,
  attachments,
  customerId  // Optional: if customer ID is known, pass it to avoid lookup
}: SendGmailEmailParams & { customerId?: string }): Promise<SendGmailEmailResult> {
  try {
    console.log('📧 Sending Gmail email:', { to, subject, businessId, aiGenerated, hasAttachments: !!attachments, isReply: !!threadId, threadId: threadId ?? '(not set)' });

    if (!supabase) {
      return { success: false, error: 'Supabase client not initialized' };
    }

    // Who is sending, stamped on the core email row.
    const sender = aiGenerated ? null : await resolveCurrentSender(businessId);

    const { data, error: invokeError } = await supabase.functions.invoke('gmail-send-email', {
      body: {
        to,
        cc,
        bcc,
        subject: subject || '',
        body,
        businessId,
        aiGenerated,
        threadId,
        inReplyTo,
        references,
        attachments,
        customerId: customerId || null,
        sender,
      },
    });

    const result = data as { success?: boolean; error?: string; messageId?: string; threadId?: string } | null;
    if (invokeError || result?.success !== true) {
      const detail = result?.error || await readInvokeError(invokeError);
      console.error('❌ Gmail send failed:', detail);
      return { success: false, error: detail };
    }

    console.log('✅ Gmail email sent successfully:', result?.messageId, 'Thread:', result?.threadId);
    
    // Record activity for CRM opportunities (non-blocking)
    recordActivityForEmail(businessId, to, subject || '', customerId).catch(err => {
      console.error('[Email] Error recording opportunity activity:', err);
    });
    
    return {
      success: true,
      messageId: result?.messageId,
      threadId: result?.threadId,
    };
  } catch (error) {
    console.error('❌ Error sending Gmail email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send email'
    };
  }
}

/**
 * Helper function to record email activity for CRM opportunities
 */
async function recordActivityForEmail(businessId: string, emailAddress: string, subject: string, knownCustomerId?: string): Promise<void> {
  try {
    if (!supabase) return;

    let customerId = knownCustomerId;

    // If customer ID not provided, look it up by email
    if (!customerId) {
      const { data: customer, error } = await supabase
        .from('customers')
        .select('id')
        .eq('business_id', businessId)
        .eq('email', emailAddress.toLowerCase().trim())
        .limit(1)
        .single();

      if (error || !customer) {
        // No customer found - nothing to record
        return;
      }

      customerId = customer.id;
    }

    // Record activity
    await recordOpportunityActivity({
      businessId,
      customerId,
      activityType: 'email_sent',
      activityData: {
        subject: subject,
        to: emailAddress
      }
    });
  } catch (error) {
    console.error('[Email Activity] Error recording activity:', error);
  }
}

async function readInvokeError(error: unknown): Promise<string> {
  const response = error && typeof error === 'object' && 'context' in error
    ? (error as { context?: Response }).context
    : undefined;
  if (response && typeof response.clone === 'function') {
    try {
      const body = await response.clone().json() as { error?: string };
      if (body?.error) return body.error;
    } catch {
      /* non-json body */
    }
  }
  return error instanceof Error ? error.message : 'Failed to send email';
}

/** Active Gmail that can send. A missing or expired connection is not an error. */
async function hasActiveGmailConnection(businessId: string): Promise<boolean> {
  if (!supabase) return false;
  const { data, error } = await supabase
    .from('gmail_connections')
    .select('id, needs_reauth')
    .eq('business_id', businessId)
    .eq('is_active', true)
    .maybeSingle();
  if (error || !data) return false;
  return data.needs_reauth !== true;
}

/**
 * Send order: the business Gmail inbox, then their verified SendGrid sender,
 * then the Nexus address (noreply@nexusbookai.com). The last step lives in
 * sendgrid-send-email, which uses noreply when no verified sender exists.
 */
export async function sendEmailWithFallback({
  businessId,
  to,
  subject,
  body,
  attachments,
  customerId,
}: {
  businessId: string;
  to: string;
  subject: string;
  body: string;
  attachments?: Array<{ filename: string; content: string; type: string }>;
  /** When set, passed to Gmail send for opportunity activity lookup (optional). */
  customerId?: string;
}): Promise<{ success: boolean; error?: string; messageId?: string; threadId?: string; method?: 'gmail' | 'sendgrid' }> {
  if (await hasActiveGmailConnection(businessId)) {
    const gmailResult = await sendGmailEmail({
      businessId,
      to,
      subject,
      body,
      attachments,
      customerId,
    });

    if (gmailResult.success) {
      return { ...gmailResult, method: 'gmail' };
    }

    console.log('Gmail send failed, continuing to SendGrid...', gmailResult.error);
  } else {
    console.log('No active Gmail connection, sending through SendGrid');
  }

  if (!supabase) {
    return { success: false, error: 'Supabase client not initialized' };
  }

  try {
    const { data, error: invokeError } = await supabase.functions.invoke('sendgrid-send-email', {
      body: {
        businessId,
        to,
        subject,
        html: body,
        attachments,
      },
    });

    const result = data as { success?: boolean; error?: string; messageId?: string } | null;
    if (invokeError || result?.success !== true) {
      const detail = result?.error || await readInvokeError(invokeError);
      console.error('SendGrid fallback invoke error:', detail);
      return { success: false, error: detail || 'SendGrid fallback failed' };
    }

    console.log('Email sent successfully via SendGrid marketing fallback');
    return { success: true, messageId: result?.messageId, method: 'sendgrid' };
  } catch (error) {
    console.error('SendGrid fallback error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Both Gmail and SendGrid sending failed',
    };
  }
}
